///
/// EarthBrowser
///
/// Created by Matt Giger on 9/15/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Single registry of per-windowType presentation and visibility metadata
/// for dataset browser windows and their drawer tabs. Adding a dataset
/// browser means adding ONE entry here; the drawer tab icon, the tab
/// visibility toggle, and the window titlebar toggle all derive from it
/// (they were previously three separate hardcoded switch statements that
/// every new dataset forgot to update).
///

import type { DatasetType } from "@/types/DatasetElement"
import {
	Activity,
	Anchor,
	Bookmark,
	Database,
	FileSpreadsheet,
	Flame,
	Folder,
	FolderTree,
	HardDrive,
	Image,
	Landmark,
	type LucideIcon,
	Map,
	Mountain,
	Package,
	Radar,
	Satellite,
	Terminal,
	TriangleAlert,
	Video,
	Wind,
} from "lucide-react"

export interface DatasetWindowMeta {
	Icon: LucideIcon
	/** Icon tint in drawer tabs and window titlebars */
	colorClass: string
	/** DatasetType behind visibleDatasets / sendDatasetAction; absent = no map-visibility toggle */
	datasetType?: DatasetType
	/** DatasetLayerManager layer toggled alongside the redux dispatch */
	layerName?: string
	/** Noun for toggle tooltips, e.g. "Hide earthquakes from map" */
	plural?: string
	/** Titlebar toggle classes while visible (tailwind needs literal class names) */
	toggleOnClass?: string
	/** Titlebar toggle focus ring */
	toggleRingClass?: string
}

/**
 * Key that ties a dataset browser window to its Layers-panel entry: the
 * window's DatasetType when it has one, else its windowType (e.g. the
 * "location" browser and the "location" layer). Compared against
 * LayerDefinition.visibility by the data dock rail.
 */
export function windowDatasetKey(windowType?: string): string | undefined {
	if (!windowType) return undefined
	return cDatasetWindowMeta[windowType]?.datasetType ?? windowType
}

export const cDatasetWindowMeta: Record<string, DatasetWindowMeta> = {
	earthquake: {
		Icon: Activity,
		colorClass: "text-red-400",
		datasetType: "earthquake",
		layerName: "earthquakes",
		plural: "earthquakes",
		toggleOnClass: "bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30",
		toggleRingClass: "focus-visible:ring-cyan-500/50",
	},
	volcano: {
		Icon: Mountain,
		colorClass: "text-orange-400",
		datasetType: "volcano",
		layerName: "volcanoes",
		plural: "volcanoes",
		toggleOnClass: "bg-orange-500/20 text-orange-400 hover:bg-orange-500/30",
		toggleRingClass: "focus-visible:ring-orange-500/50",
	},
	buoy: {
		Icon: Anchor,
		colorClass: "text-cyan-400",
		datasetType: "buoy",
		layerName: "buoys",
		plural: "buoys",
		toggleOnClass: "bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30",
		toggleRingClass: "focus-visible:ring-cyan-500/50",
	},
	webcam: {
		Icon: Video,
		colorClass: "text-fuchsia-400",
		datasetType: "webcam",
		layerName: "webcams",
		plural: "webcams",
		toggleOnClass: "bg-fuchsia-500/20 text-fuchsia-400 hover:bg-fuchsia-500/30",
		toggleRingClass: "focus-visible:ring-fuchsia-500/50",
	},
	wildfire: {
		Icon: Flame,
		colorClass: "text-rose-500",
		datasetType: "wildfire",
		layerName: "wildfires",
		plural: "wildfires",
		toggleOnClass: "bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30",
		toggleRingClass: "focus-visible:ring-cyan-500/50",
	},
	hurricane: {
		Icon: Wind,
		colorClass: "text-sky-400",
		datasetType: "hurricane",
		layerName: "hurricanes",
		plural: "hurricanes",
		toggleOnClass: "bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30",
		toggleRingClass: "focus-visible:ring-cyan-500/50",
	},
	alert: {
		Icon: TriangleAlert,
		colorClass: "text-amber-400",
		datasetType: "alert",
		layerName: "alerts",
		plural: "weather alerts",
		toggleOnClass: "bg-amber-500/20 text-amber-400 hover:bg-amber-500/30",
		toggleRingClass: "focus-visible:ring-amber-500/50",
	},
	satellite: {
		Icon: Satellite,
		colorClass: "text-purple-400",
		datasetType: "satellite",
		layerName: "satellites",
		plural: "satellites",
		toggleOnClass: "bg-purple-500/20 text-purple-400 hover:bg-purple-500/30",
		toggleRingClass: "focus-visible:ring-purple-500/50",
	},
	radar: {
		Icon: Radar,
		colorClass: "text-green-400",
		datasetType: "nexrad",
		plural: "radar",
		toggleOnClass: "bg-green-500/20 text-green-400 hover:bg-green-500/30",
		toggleRingClass: "focus-visible:ring-green-500/50",
	},

	// Icon-only window types (no dataset visibility toggle)
	location: { Icon: Landmark, colorClass: "text-blue-400" },
	"saved-views": { Icon: Bookmark, colorClass: "text-cyan-400" },
	csv: { Icon: FileSpreadsheet, colorClass: "text-green-400" },
	shapefile: { Icon: Map, colorClass: "text-blue-400" },
	geojson: { Icon: Map, colorClass: "text-blue-400" },
	image: { Icon: Image, colorClass: "text-teal-400" },
	geopackage: { Icon: Database, colorClass: "text-purple-400" },
	"geopackage-catalog": { Icon: Database, colorClass: "text-cyan-400" },
	"sql-terminal": { Icon: Terminal, colorClass: "text-yellow-400" },
	"storage-manager": { Icon: HardDrive, colorClass: "text-purple-400" },
	"ogc-collection": { Icon: Database, colorClass: "text-cyan-400" },
	"remote-packages": { Icon: Package, colorClass: "text-green-400" },
	"local-packages": { Icon: Folder, colorClass: "text-purple-400" },
	"local-packages-browser": { Icon: FileSpreadsheet, colorClass: "text-purple-400" },
	"file-browser": { Icon: FolderTree, colorClass: "text-green-500" },
}
